import { ThemeProvider, theme } from "@primer/react";

import deepmerge from "deepmerge";
import { useEffect } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { BrowserRouter as Router } from "react-router-dom";
import "./App.scss";
import { appConst } from "./AppConst";
import BaseStyles from "./BaseStyles";
import { LazyPage } from "./components-ui/loadable";
import { CommonProvider } from "./contexts/common";
import { HubProvider } from "./contexts/HubProvider";
import { useAppDispatch } from "./hooks/useAppDispatch";
import { useAppSelector } from "./hooks/useAppSelector";
import { rootActions } from "./state/actions/rootActions";
import { apiClient, setOnSessionExpired } from "./api/apiClient";
import { DUONG_DAN_CHIA_SE } from "./api/core/quanLyApiApi";
import "./helpers/string.extensions";

// Trang tài liệu API chia sẻ cho đối tác (/tai-lieu-api/{ma}) là trang CÔNG KHAI: vẽ thẳng, KHÔNG đi qua
// luồng đăng nhập — nếu để rơi vào UnAuthorizedContent thì đối tác bị đẩy sang trang đăng nhập OIDC.
const LA_TRANG_CHIA_SE = window.location.pathname.toLowerCase().startsWith(DUONG_DAN_CHIA_SE);
const TaiLieuApiChiaSePage = LazyPage(import("./pages/tai-lieu-api-chia-se/TaiLieuApiChiaSePage"));

const AuthorizedContent = LazyPage(import("./content/AuthorizedContent"));
const UnAuthorizedContent = LazyPage(import("./content/UnAuthorizedContent"));
const UnAuthorizedContentVNPT = LazyPage(
  import("./content/UnAuthorizedContentVNPT"),
);

const customTheme = deepmerge(
  {
    ...theme,
    fontSizes: [
      "11px",
      "13px",
      "16px",
      "20px",
      "24px",
      "32px",
      "40px",
      "48px",
      "56px",
    ],
  },
  {
    colorSchemes: {
      light: {
        colors: {
          accent: {
            fg: "var(--primary)",
            emphasis: "var(--primary)",
          },
          // --bgColor-accent-emphasis
          btn: {
            primary: {
              bg: "var(--primary)",
              focusBg: "var(--primary)",
              hoverBg: "var(--primary)",
              selectedBg: "var(--primary)",
              disabledBg: "rgba(222, 63, 15,0.6)",
            },
          },
        },
      },
    },
  },
);

function App() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((x) => x.auth);
  const { themeMode } = useAppSelector((x) => x.common.layout);

  // Hết phiên (API 401 + refresh thất bại) → xóa user → App tự hiển thị màn đăng nhập.
  useEffect(() => {
    setOnSessionExpired(() =>
      dispatch(rootActions.auth.GET_USER_INFO_ERROR("")),
    );
    return () => setOnSessionExpired(null);
  }, [dispatch]);

  useEffect(() => {
    if (LA_TRANG_CHIA_SE) return;
    if (appConst.OIDC_UNIT === "vnpt") {
      dispatch(rootActions.auth.GET_USER_INFO_VNPT_START(undefined));
    } else {
      dispatch(rootActions.auth.GET_USER_INFO_START(undefined));
    }

    const faviconPath = appConst.LOGO || "/favicon.ico";
    const link: HTMLLinkElement =
      document.querySelector("link[rel*='icon']") ||
      document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    link.href = faviconPath;
    document.head.appendChild(link);
  }, []);

  // Nạp cấu hình bật/tắt menu khi KHÔNG bật phân quyền menu (menu lấy từ menusSample).
  // Phân quyền BẬT (=1) thì menu lấy từ server -> bỏ qua cấu hình này.
  useEffect(() => {
    if (!user) return;
    if (appConst.MENU_PHAN_QUYEN && appConst.MENU_PHAN_QUYEN !== "0") return;
    apiClient
      .get("cau-hinh-menu")
      .then((res) => {
        dispatch(
          rootActions.auth.SET_MENU_OVERRIDES(
            (res?.is_success ? res.data?.overrides : null) ?? {},
          ),
        );
      })
      .catch(() => {
        // Lỗi nạp cấu hình -> dùng menusSample mặc định. VẪN phải dispatch để
        // PrivateContent biết "đã nạp xong" mà mở khoá route (nếu không sẽ treo màn trắng).
        dispatch(rootActions.auth.SET_MENU_OVERRIDES({}));
      });
  }, [user]);

  return (
    <Router>
      <style>{`
        :root {
          --bg_header: url(/imageStyle/${appConst.THEME}/banner.png);
          --bg_header_left: url(/imageStyle/${appConst.THEME}/bannerLeft.png); 
          --primary: ${appConst.THEME_COLOR_PRIMARY};
          --text_parent: ${appConst.THEME_COLOR_TEXT_PARENT};
          --bg_hover: ${appConst.THEME_COLOR_HOVER};
          --primary_hover: ${appConst.THEME_COLOR_PRIMARY_HOVER};
          /* Thanh cuộn màu XÁM trung tính (người dùng chốt 16/09/2026 — không theo màu chủ đạo).
             Rãnh TRONG SUỐT: rãnh có màu hiện thành một dải kẻ chạy dọc mép sidebar/khung lọc. */
          --background_scrollbar_track: transparent;
          --background_scrollbar_thumb: #c1c1c1;
          --background_scrollbar_thumb_hover: #a8a8a8;
          --color_product: ${appConst.THEME_COLOR_PRODUCT};
          --color_header: ${appConst.THEME_COLOR_HEADER || "#fff"};
          /* Nền của kiểu giao diện "Nền tối" — dự phòng cho trình duyệt chưa có color-mix. */
          --nen_toi: #0d1117;
          /* Ảnh vân nền thanh trên cùng của kiểu "Thanh lịch".
             ⚠ PHẢI khai ở đây chứ KHÔNG viết url() thẳng trong file .module.css: css-loader
             coi mọi url() là một module cần resolve, đường dẫn gốc /images/... nằm ở thư mục
             public nên nó tìm trong src/layout rồi báo "Module not found". Khai bằng JS thì
             chuỗi đi thẳng vào thẻ <style>, css-loader không đụng tới. */
          --anh_nen_header: url(/images/header3.png);
        }
        /* Có color-mix thì pha màu chủ đạo vào nền gần đen: vẫn tối rõ, mà đổi màu ở màn
           Cấu hình là thấy đổi tông ngay. Khai MỘT chỗ, cột menu và menu dùng chung. */
        @supports (color: color-mix(in srgb, red 10%, blue)) {
          :root {
            --nen_toi: color-mix(in srgb, var(--primary) 10%, #090c11);
          }
        }
    `}</style>
      <CommonProvider>
        <ThemeProvider theme={customTheme} colorMode={themeMode}>
          <BaseStyles>
            {LA_TRANG_CHIA_SE && <TaiLieuApiChiaSePage />}
            {!LA_TRANG_CHIA_SE && user && (
              <>
                <HubProvider>
                  <AuthorizedContent />
                </HubProvider>
              </>
            )}
            {!LA_TRANG_CHIA_SE &&
              !user &&
              (appConst.OIDC_UNIT === "vnpt" ? (
                <UnAuthorizedContentVNPT />
              ) : (
                <UnAuthorizedContent />
              ))}
          </BaseStyles>
        </ThemeProvider>
      </CommonProvider>
    </Router>
  );
}
export default App;
